import { Link } from 'react-router-dom';
import type { AssistantAlertsData } from '@cumpli/shared';
import { Pill, type Tone } from '../../ui';
import { CardHeader, CardShell } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';
import { shortDate } from './labels';

const PRIORITY_TONE: Record<'Alta' | 'Media' | 'Baja', Tone> = {
  Alta: 'red',
  Media: 'amber',
  Baja: 'slate',
};

/**
 * `list_alerts` — recent alerts as tappable rows (title + priority pill + short
 * opened date), each deep-linking to its alert. `id` is PUBLIC so the row is
 * always navigable.
 */
export function AlertsCard({ data }: { data: AssistantAlertsData }) {
  const header =
    data.count === 0
      ? 'Sin alertas'
      : `${data.count} ${data.count === 1 ? 'alerta' : 'alertas'}`;

  return (
    <CardShell>
      <CardHeader title={header} label="Alertas" />

      {data.alerts.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {data.alerts.map((a) => (
            <Link
              key={a.id}
              to={`/alertas/${a.id}`}
              className="flex items-center gap-2 rounded-lg bg-canvas px-2.5 py-2 transition-colors hover:bg-line/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                <MaybeRedacted value={a.title} />
              </span>
              <Pill tone={isRedacted(a.priority) ? 'slate' : PRIORITY_TONE[a.priority]}>
                <MaybeRedacted value={a.priority} />
              </Pill>
              <span className="shrink-0 font-mono text-[10px] text-ink-faint">
                <MaybeRedacted value={a.openedAt} render={shortDate} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </CardShell>
  );
}
